import { c as _c } from "react/compiler-runtime";
import { Badge, Box, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import { ADVISOR_FEATURE_MANAGE_ENABLED, ADVISOR_FEATURE_RECRUIT_ENABLED, ADVISOR_FEATURE_TOOLS_CHROME_ENABLED, ADVISORS_FEATURES_ENABLED, WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants';
import { useDirectChatUnreadCount } from '@src/components/sco/Advisors/hooks/useDirectChatUnreadCount';
import { BecomeAdvisorNavCard } from '@src/components/sco/Advisors/layout/BecomeAdvisorNavCard.tsx';
import { NavGroup } from '@src/components/sco/Advisors/layout/NavGroup';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { isAdvisorProfileHubEligible, isAdvisorProfileHubPath, isPublicAdvisorProfilePath } from '@src/components/sco/Advisors/utils/advisorProfileHub';
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { LuCalculator, LuFileText, LuIdCard, LuMessageSquare, LuPhoneCall, LuRocket, LuSearch, LuSparkles, LuTarget, LuUsers } from 'react-icons/lu';
const normalizePath = (path: string) => path.split('?')[0].split('#')[0];

/**
 * Inbox nav row with the aggregate unread badge.
 *
 * `SidebarNestedNavListSection` declares a `badgeProps` field but never renders
 * it, so the count rides on the ReactNode `title` instead.
 */
const InboxNavTitle = t0 => {
  const $ = _c(5);
  const {
    unreadCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Text flex={1} lineClamp={1} minWidth={0}>Messages</Text>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== unreadCount) {
    t2 = unreadCount > 0 && <Badge flexShrink={0} variant="count">{unreadCount}</Badge>;
    $[1] = unreadCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <HStack align="center" gap={2} minWidth={0} width="full">{t1}{t2}</HStack>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const isPathActive = (asPath: string, href: string, options?: {
  exact?: boolean;
  alsoActive?: string[];
}) => {
  const path = normalizePath(asPath);
  const paths = [href, ...(options?.alsoActive ?? [])];
  return paths.some(p => options?.exact ? path === p : path === p || p.length > 1 && path.startsWith(`${p}/`));
};
const isSessionsListActive = (asPath: string, sessionsPath: string) => {
  const path = normalizePath(asPath);
  if (path === sessionsPath) {
    return true;
  }
  if (!path.startsWith(`${sessionsPath}/`)) {
    return false;
  }
  // Sub-routes that are not "My sessions" list chrome still live under Sessions nav.
  if (path === `${sessionsPath}/new`) {
    return true;
  }
  if (path === `${sessionsPath}/settings`) {
    return false;
  }
  return true;
};

/**
 * Capability-gated Advisors left nav (no Founder/Advisor mode toggle).
 *
 * - Conversations — board chats always; founder engagements when advisor-capable
 * - Manage / Recruit / Tools — demand items always; supply items added when advisor-capable
 * - Become card — non-advisors only
 * - Hrefs follow `useAdvisorsPaths()` so Request Workspace embeds stay under manage/...
 */
export const AdvisorsNav = () => {
  const $ = _c(107);
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const asPath = router.asPath;
  const paths = useAdvisorsPaths();
  const {
    unreadCount
  } = useDirectChatUnreadCount();
  const isMarketplaceAdvisor = !!currentUser?.advisorProfile?.isApproved;
  const canUseAdvisorItems = isMarketplaceAdvisor;
  const t0 = currentUser?.advisorProfile?.statusKey;
  let t1;
  if ($[0] !== t0) {
    t1 = isAdvisorProfileHubEligible(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasAdvisorProfileHub = t1;
  const showBecomeAdvisorCard = !hasAdvisorProfileHub;
  let t2;
  if ($[2] !== asPath || $[3] !== paths.sessions) {
    t2 = isSessionsListActive(asPath, paths.sessions);
    $[2] = asPath;
    $[3] = paths.sessions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sessionsActive = t2;
  const t3 = paths.ask;
  let t4;
  if ($[5] !== asPath || $[6] !== paths.ask) {
    t4 = isPathActive(asPath, paths.ask, {
      exact: true
    });
    $[5] = asPath;
    $[6] = paths.ask;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== paths.ask || $[9] !== t4) {
    t5 = {
      title: WIL_DISPLAY_NAME,
      href: t3,
      icon: LuSparkles,
      isActive: t4
    };
    $[8] = paths.ask;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== unreadCount) {
    t6 = <InboxNavTitle unreadCount={unreadCount} />;
    $[11] = unreadCount;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = paths.inbox;
  let t8;
  if ($[13] !== asPath || $[14] !== paths.inbox) {
    t8 = isPathActive(asPath, paths.inbox);
    $[13] = asPath;
    $[14] = paths.inbox;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== paths.inbox || $[17] !== t6 || $[18] !== t8) {
    t9 = {
      title: t6,
      href: t7,
      icon: LuMessageSquare,
      isActive: t8
    };
    $[16] = paths.inbox;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== paths.sessions || $[21] !== sessionsActive) {
    t10 = ADVISORS_FEATURES_ENABLED && {
      title: "Sessions",
      href: paths.sessions,
      icon: LuPhoneCall,
      isActive: sessionsActive
    };
    $[20] = paths.sessions;
    $[21] = sessionsActive;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t5 || $[25] !== t9) {
    t11 = [t5, t9, t10].filter(Boolean);
    $[23] = t10;
    $[24] = t5;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const primarySections = t11;
  let t12;
  if ($[27] !== asPath || $[28] !== paths.agreement || $[29] !== paths.agreements || $[30] !== paths.sign) {
    t12 = isPathActive(asPath, paths.agreements, {
      alsoActive: [paths.sign, paths.agreement]
    });
    $[27] = asPath;
    $[28] = paths.agreement;
    $[29] = paths.agreements;
    $[30] = paths.sign;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const onAgreementDetail = t12;
  const t13 = paths.myAdvisors;
  let t14;
  if ($[32] !== asPath || $[33] !== paths.myAdvisors || $[34] !== paths.seats) {
    t14 = isPathActive(asPath, paths.myAdvisors, {
      alsoActive: [paths.seats]
    });
    $[32] = asPath;
    $[33] = paths.myAdvisors;
    $[34] = paths.seats;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== paths.myAdvisors || $[37] !== t14) {
    t15 = {
      title: "My Advisors",
      href: t13,
      icon: LuUsers,
      isActive: t14
    };
    $[36] = paths.myAdvisors;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== asPath || $[40] !== canUseAdvisorItems || $[41] !== paths.myFounders) {
    t16 = canUseAdvisorItems ? [{
      title: "My Founders",
      href: paths.myFounders,
      icon: LuRocket,
      isActive: isPathActive(asPath, paths.myFounders)
    }] : [];
    $[39] = asPath;
    $[40] = canUseAdvisorItems;
    $[41] = paths.myFounders;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== onAgreementDetail || $[44] !== paths.agreements) {
    t17 = {
      title: "FAST Agreements",
      href: paths.agreements,
      icon: LuFileText,
      isActive: onAgreementDetail
    };
    $[43] = onAgreementDetail;
    $[44] = paths.agreements;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t15 || $[47] !== t16 || $[48] !== t17) {
    t18 = [t15, ...t16, t17];
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const manageSections = t18;
  const t19 = paths.ownProfile;
  let t20;
  if ($[50] !== asPath || $[51] !== paths.ownProfile) {
    t20 = isAdvisorProfileHubPath(asPath, paths.ownProfile);
    $[50] = asPath;
    $[51] = paths.ownProfile;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== paths.ownProfile || $[54] !== t20) {
    t21 = {
      title: "My Advisor Profile",
      href: t19,
      icon: LuIdCard,
      isActive: t20
    };
    $[53] = paths.ownProfile;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  const ownProfileNavItem = t21;
  let t22;
  bb0: {
    if (!ADVISOR_FEATURE_RECRUIT_ENABLED) {
      let t23;
      if ($[56] !== hasAdvisorProfileHub || $[57] !== ownProfileNavItem) {
        t23 = hasAdvisorProfileHub ? [ownProfileNavItem] : [];
        $[56] = hasAdvisorProfileHub;
        $[57] = ownProfileNavItem;
        $[58] = t23;
      } else {
        t23 = $[58];
      }
      t22 = t23;
      break bb0;
    }
    let t23;
    if ($[59] !== asPath || $[60] !== paths.browse || $[61] !== paths.directory || $[62] !== paths.profile) {
      t23 = isPathActive(asPath, paths.directory, {
        alsoActive: [paths.browse]
      }) || isPublicAdvisorProfilePath(asPath, paths.profile);
      $[59] = asPath;
      $[60] = paths.browse;
      $[61] = paths.directory;
      $[62] = paths.profile;
      $[63] = t23;
    } else {
      t23 = $[63];
    }
    let t24;
    if ($[64] !== paths.directory || $[65] !== t23) {
      t24 = {
        title: "Find Advisors",
        href: paths.directory,
        icon: LuSearch,
        isActive: t23
      };
      $[64] = paths.directory;
      $[65] = t23;
      $[66] = t24;
    } else {
      t24 = $[66];
    }
    let t25;
    if ($[67] !== asPath || $[68] !== canUseAdvisorItems || $[69] !== paths.opportunities) {
      t25 = canUseAdvisorItems ? [{
        title: "Opportunities",
        href: paths.opportunities,
        icon: LuTarget,
        isActive: isPathActive(asPath, paths.opportunities)
      }] : [];
      $[67] = asPath;
      $[68] = canUseAdvisorItems;
      $[69] = paths.opportunities;
      $[70] = t25;
    } else {
      t25 = $[70];
    }
    let t26;
    if ($[71] !== hasAdvisorProfileHub || $[72] !== ownProfileNavItem) {
      t26 = hasAdvisorProfileHub ? [ownProfileNavItem] : [];
      $[71] = hasAdvisorProfileHub;
      $[72] = ownProfileNavItem;
      $[73] = t26;
    } else {
      t26 = $[73];
    }
    let t27;
    if ($[74] !== t24 || $[75] !== t25 || $[76] !== t26) {
      t27 = [t24, ...t25, ...t26];
      $[74] = t24;
      $[75] = t25;
      $[76] = t26;
      $[77] = t27;
    } else {
      t27 = $[77];
    }
    t22 = t27;
  }
  const recruitSections = t22;
  let t23;
  bb1: {
    if (!ADVISOR_FEATURE_MANAGE_ENABLED) {
      let t24;
      if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = [];
        $[78] = t24;
      } else {
        t24 = $[78];
      }
      t23 = t24;
      break bb1;
    }
    const t24 = paths.compCalculator;
    let t25;
    if ($[79] !== asPath || $[80] !== paths.compCalculator) {
      t25 = isPathActive(asPath, paths.compCalculator, {
        exact: true
      });
      $[79] = asPath;
      $[80] = paths.compCalculator;
      $[81] = t25;
    } else {
      t25 = $[81];
    }
    let t26;
    if ($[82] !== paths.compCalculator || $[83] !== t25) {
      t26 = [{
        title: "Comp Calculator",
        href: t24,
        icon: LuCalculator,
        isActive: t25
      }];
      $[82] = paths.compCalculator;
      $[83] = t25;
      $[84] = t26;
    } else {
      t26 = $[84];
    }
    t23 = t26;
  }
  const toolsSections = t23;
  let t24;
  if ($[85] !== primarySections) {
    t24 = <NavGroup label="Ask"><SidebarNestedNavList accordionBehavior="expand-only" sections={primarySections} /></NavGroup>;
    $[85] = primarySections;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== canUseAdvisorItems || $[88] !== manageSections) {
    t25 = !ADVISOR_FEATURE_TOOLS_CHROME_ENABLED && canUseAdvisorItems && <NavGroup label="Manage"><SidebarNestedNavList accordionBehavior="expand-only" sections={[manageSections[1]]} /></NavGroup>;
    $[87] = canUseAdvisorItems;
    $[88] = manageSections;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== manageSections || $[91] !== recruitSections || $[92] !== toolsSections) {
    t26 = ADVISOR_FEATURE_TOOLS_CHROME_ENABLED && <><NavGroup label="Manage"><SidebarNestedNavList accordionBehavior="expand-only" sections={manageSections} /></NavGroup>{recruitSections.length > 0 && <NavGroup label="Recruit"><SidebarNestedNavList accordionBehavior="expand-only" sections={recruitSections} /></NavGroup>}{toolsSections.length > 0 && <NavGroup label="Tools"><SidebarNestedNavList accordionBehavior="expand-only" sections={toolsSections} /></NavGroup>}</>;
    $[90] = manageSections;
    $[91] = recruitSections;
    $[92] = toolsSections;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== hasAdvisorProfileHub || $[95] !== ownProfileNavItem) {
    t27 = !ADVISOR_FEATURE_TOOLS_CHROME_ENABLED && hasAdvisorProfileHub && <><NavGroup label="Manage"><SidebarNestedNavList accordionBehavior="expand-only" sections={[ownProfileNavItem]} /></NavGroup></>;
    $[94] = hasAdvisorProfileHub;
    $[95] = ownProfileNavItem;
    $[96] = t27;
  } else {
    t27 = $[96];
  }
  let t28;
  if ($[97] !== t24 || $[98] !== t25 || $[99] !== t26 || $[100] !== t27) {
    t28 = <Box flex={1} minHeight={0} overflowY="auto" paddingX={1} paddingY={4} width="full"><VStack align="stretch" gap={5} width="full">{t24}{t25}{t26}{t27}</VStack></Box>;
    $[97] = t24;
    $[98] = t25;
    $[99] = t26;
    $[100] = t27;
    $[101] = t28;
  } else {
    t28 = $[101];
  }
  let t29;
  if ($[102] !== showBecomeAdvisorCard) {
    t29 = showBecomeAdvisorCard && <><Separator /><Box marginTop={3} paddingX={1} width="full"><BecomeAdvisorNavCard /></Box></>;
    $[102] = showBecomeAdvisorCard;
    $[103] = t29;
  } else {
    t29 = $[103];
  }
  let t30;
  if ($[104] !== t28 || $[105] !== t29) {
    t30 = <VStack align="stretch" gap={0} height="full" minHeight={0} width="full">{t28}{t29}</VStack>;
    $[104] = t28;
    $[105] = t29;
    $[106] = t30;
  } else {
    t30 = $[106];
  }
  return t30;
};